import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Alert,
  CircularProgress,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  Pagination
} from '@mui/material';
import {
  Search as SearchIcon,
  FolderShared as FolderSharedIcon,
  OpenInNew as OpenInNewIcon,
  Refresh as RefreshIcon,
  People as PeopleIcon,
  Lock as LockIcon,
  Public as PublicIcon,
  Close as CloseIcon,
  Explore as ExploreIcon,
  Backup as BackupIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  CloudOff as CloudOffIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import DirectoryNavigator from './DirectoryNavigator';
import SharedDriveBackupModal from './SharedDriveBackupModal';
import SharedDrivesGlobalSearch from './SharedDrivesGlobalSearch';
import api from '../../services/api';

// eslint-disable-next-line max-lines-per-function
export default function SharedDrives() {
  useAuth();
  const [sharedDrives, setSharedDrives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [navigatorDialog, setNavigatorDialog] = useState(false);
  const [navigatingDrive, setNavigatingDrive] = useState(null);
  const [backupModalOpen, setBackupModalOpen] = useState(false);
  const [backupDrive, setBackupDrive] = useState(null);
  const [backupStatuses, setBackupStatuses] = useState({});
  const [_loadingBackupStatus, setLoadingBackupStatus] = useState(false);
  const [globalSearchOpen, setGlobalSearchOpen] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 100,  // Show more drives without needing pagination
    total: 0,
    nextPageToken: null
  });

  useEffect(() => {
    fetchSharedDrives();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSharedDrives = async (pageToken = null, search = '') => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        pageSize: pagination.pageSize,
        ...(pageToken && { pageToken }),
        ...(search && { q: `name contains '${search}'` })
      };

      const response = await api.getSharedDrives(params);

      setSharedDrives(response.sharedDrives || []);
      setPagination(prev => ({
        ...prev,
        total: response.sharedDrives?.length || 0,
        nextPageToken: response.nextPageToken
      }));

      // Fetch backup statuses for all drives
      if (response.sharedDrives && response.sharedDrives.length > 0) {
        await fetchBackupStatuses(response.sharedDrives.map(drive => drive.id));
      }
    } catch (err) {
      console.error('Error fetching shared drives:', err);
      setError(err.message || 'Failed to load shared drives');
    } finally {
      setLoading(false);
    }
  };

  const fetchBackupStatuses = async (driveIds) => {
    try {
      setLoadingBackupStatus(true);
      const statuses = await api.getSharedDriveBackupStatusBatch(driveIds);
      setBackupStatuses(statuses);
    } catch (err) {
      console.error('Error fetching backup statuses:', err);
      // Don't show error to user, just continue without backup status
    } finally {
      setLoadingBackupStatus(false);
    }
  };

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchSharedDrives(null, searchQuery);
  };

  const handleSearchKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handleRefresh = () => {
    setSearchQuery('');
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchSharedDrives();
  };


  const handleOpenDrive = (driveId) => {
    window.open(`https://drive.google.com/drive/folders/${driveId}`, '_blank');
  };

  const handleBrowseDrive = (drive) => {
    setNavigatingDrive(drive);
    setNavigatorDialog(true);
  };

  const handleCloseNavigator = () => {
    setNavigatorDialog(false);
    setNavigatingDrive(null);
  };

  const handleOpenBackup = (drive) => {
    setBackupDrive(drive);
    setBackupModalOpen(true);
  };

  const handleCloseBackup = () => {
    setBackupModalOpen(false);
    setBackupDrive(null);
    // Refresh backup statuses after modal closes
    if (sharedDrives.length > 0) {
      fetchBackupStatuses(sharedDrives.map(drive => drive.id));
    }
  };

  const formatDate = (dateInput) => {
    if (!dateInput) return 'Unknown';

    // Handle Firestore timestamp
    if (dateInput._seconds) {
      return new Date(dateInput._seconds * 1000).toLocaleString();
    }

    // Handle regular date string
    return new Date(dateInput).toLocaleDateString();
  };


  const renderBackupStatus = (driveId) => {
    const status = backupStatuses[driveId];
    if (!status) return null;

    const lastBackup = status.lastBackup;
    const isEnabled = status.scheduledBackupEnabled;

    return (
      <Box sx={{ mt: 2, p: 1.5, bgcolor: 'background.default', borderRadius: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          {isEnabled ? (
            <>
              <BackupIcon sx={{ fontSize: 18, mr: 0.5, color: 'success.main' }} />
              <Typography variant="caption" color="success.main" sx={{ fontWeight: 500 }}>
                Backup Enabled
              </Typography>
            </>
          ) : (
            <>
              <CloudOffIcon sx={{ fontSize: 18, mr: 0.5, color: 'text.disabled' }} />
              <Typography variant="caption" color="text.disabled">
                Backup Disabled
              </Typography>
            </>
          )}
        </Box>

        {lastBackup && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {lastBackup.status === 'success' ? (
              <CheckCircleIcon sx={{ fontSize: 16, mr: 0.5, color: 'success.main' }} />
            ) : lastBackup.status === 'failed' ? (
              <ErrorIcon sx={{ fontSize: 16, mr: 0.5, color: 'error.main' }} />
            ) : (
              <ScheduleIcon sx={{ fontSize: 16, mr: 0.5, color: 'warning.main' }} />
            )}
            <Typography variant="caption" color="text.secondary">
              Last: {formatDate(lastBackup.timestamp)}
              {lastBackup.stats && ` • ${lastBackup.stats.backedUpFiles || 0} files`}
            </Typography>
          </Box>
        )}

        {!lastBackup && isEnabled && (
          <Typography variant="caption" color="text.secondary">
            No backups yet • Scheduled nightly at 10 PM
          </Typography>
        )}
      </Box>
    );
  };

  const renderDriveCard = (drive) => (
    <Card key={drive.id} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <FolderSharedIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="h6" component="h3" noWrap>
            {drive.name}
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" gutterBottom>
          Created: {formatDate(drive.createdTime)}
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 2 }}>
          {drive.restrictions?.domainUsersOnly && (
            <Chip
              label="Domain Only"
              size="small"
              icon={<LockIcon />}
              color="warning"
              variant="outlined"
            />
          )}
          {drive.restrictions?.driveMembersOnly && (
            <Chip
              label="Members Only"
              size="small"
              icon={<PeopleIcon />}
              color="info"
              variant="outlined"
            />
          )}
          {!drive.restrictions?.domainUsersOnly && !drive.restrictions?.driveMembersOnly && (
            <Chip
              label="Open Access"
              size="small"
              icon={<PublicIcon />}
              color="success"
              variant="outlined"
            />
          )}
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Capabilities:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
            {drive.capabilities?.canEdit && (
              <Chip label="Edit" size="small" variant="outlined" />
            )}
            {drive.capabilities?.canComment && (
              <Chip label="Comment" size="small" variant="outlined" />
            )}
            {drive.capabilities?.canShare && (
              <Chip label="Share" size="small" variant="outlined" />
            )}
            {drive.capabilities?.canDownload && (
              <Chip label="Download" size="small" variant="outlined" />
            )}
          </Box>
        </Box>

        {/* Backup Status */}
        {renderBackupStatus(drive.id)}
      </CardContent>

      <CardActions>
        <Button
          size="small"
          startIcon={<ExploreIcon />}
          onClick={() => handleBrowseDrive(drive)}
          variant="contained"
          color="primary"
        >
          Browse
        </Button>
        <Button
          size="small"
          startIcon={<BackupIcon />}
          onClick={() => handleOpenBackup(drive)}
          variant="outlined"
        >
          Backup
        </Button>
        <Button
          size="small"
          startIcon={<OpenInNewIcon />}
          onClick={() => handleOpenDrive(drive.id)}
        >
          Open Drive
        </Button>
      </CardActions>
    </Card>
  );


  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">
          Shared Google Drives
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<SearchIcon />}
            onClick={() => setGlobalSearchOpen(true)}
            size="small"
          >
            Search All Drives
          </Button>
          <Tooltip title="Refresh drives list">
            <IconButton onClick={handleRefresh} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      {/* Search */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search shared drives..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyPress={handleSearchKeyPress}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleSearch}>
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
      </Box>
      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      {/* Loading State */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Empty State */}
          {sharedDrives.length === 0 ? (
            <Alert severity="info">
              {searchQuery
                ? `No shared drives found matching "${searchQuery}"`
                : 'No shared drives found. You may need to be added to shared drives by your administrator.'
              }
            </Alert>
          ) : (
            <>
              {/* Drive Cards */}
              <Grid container spacing={3}>
                {sharedDrives.map(drive => (
                  <Grid
                    key={drive.id}
                    size={{
                      xs: 12,
                      sm: 6,
                      md: 4
                    }}>
                    {renderDriveCard(drive)}
                  </Grid>
                ))}
              </Grid>

              {/* Pagination - if needed for large datasets */}
              {pagination.total > pagination.pageSize && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    count={Math.ceil(pagination.total / pagination.pageSize)}
                    page={pagination.page}
                    onChange={(event, value) => {
                      setPagination(prev => ({ ...prev, page: value }));
                      // Note: Google Drive API uses pageTokens, not page numbers
                      // This would need additional logic to track tokens for each page
                    }}
                  />
                </Box>
              )}
            </>
          )}
        </>
      )}
      {/* Directory Navigator Dialog */}
      <Dialog
        open={navigatorDialog}
        onClose={handleCloseNavigator}
        maxWidth="xl"
        fullWidth
        fullScreen
        PaperProps={{
          sx: {
            height: '100vh',
            maxHeight: '100vh',
            m: 0,
            borderRadius: 0
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FolderSharedIcon />
            <Typography variant="h6">
              {navigatingDrive?.name || 'Directory Browser'}
            </Typography>
          </Box>
          <IconButton onClick={handleCloseNavigator}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: 'calc(100vh - 64px)', overflow: 'hidden' }}>
          {navigatingDrive && (
            <DirectoryNavigator
              selectedDrive={navigatingDrive}
              onClose={handleCloseNavigator}
            />
          )}
        </DialogContent>
      </Dialog>
      {/* Backup Modal */}
      <SharedDriveBackupModal
        open={backupModalOpen}
        onClose={handleCloseBackup}
        drive={backupDrive}
      />
      {/* Global Search Modal */}
      <SharedDrivesGlobalSearch
        open={globalSearchOpen}
        onClose={() => setGlobalSearchOpen(false)}
        preSelectedDrives={sharedDrives.map(drive => drive.id)}
      />
    </Box>
  );
}
